import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AgentInfo} from '../types/session'
import {isCLIAgentId} from '../utils/cli'

export function generateAgentInfoQueryKey(agentId: number | undefined | null, isCliAgent: boolean) {
  return ['agent-info', agentId, isCliAgent]
}

// Hardcoded info for CLI sessions since they don't have agent metadata in the monolith
const CLI_AGENT_INFO: AgentInfo = {
  display_name: 'Copilot CLI',
  slug: 'copilot-cli',
  avatar_url: '',
  doc_url: '',
}

/**
 *
 * This hook fetches agent metadata (name, avatar, etc.) from the monolith
 * for agents that are returned from CAPI sessions but don't include
 * full agent information.
 *
 * For CLI sessions, returns hardcoded info since the CLI agent isn't
 * registered in the monolith's agent registry.
 */
export function useAgentInfo(agentId: number | undefined | null) {
  const isCliAgent = isCLIAgentId(agentId ?? undefined)

  return useQuery<AgentInfo | null>({
    queryKey: generateAgentInfoQueryKey(agentId, isCliAgent),
    queryFn: async () => {
      // Return hardcoded info for CLI agents
      if (isCliAgent) {
        return CLI_AGENT_INFO
      }

      const response = await reactFetchJSON(`/copilot/agent-sessions/agents/${agentId}`)

      if (!response.ok) {
        // For server errors (5xx), throw to allow retries since they're transient.
        if (response.status >= 500) {
          throw new Error(`Failed to fetch agent info for agent ${agentId}: ${response.status}`)
        }
        // For client errors (e.g. 404), return null to prevent infinite refetch loops.
        // TanStack Query v5 treats queries with data === undefined as always stale,
        // so throwing would cause repeated refetches regardless of staleTime.
        return null
      }

      const data = await response.json()
      return data.agent_info
    },
    enabled: !!agentId,
    staleTime: Infinity,
    retry: 3,
  })
}
